/**
 * Reports.
 *
 * Six periods behind one route (`/reports/:period`), each rendering the same
 * `ReportView` over a different window. Tabs rather than a dropdown, because
 * comparing "this week" against "this month" is a two-click round trip an
 * owner makes constantly, and a select box makes it four.
 */

import type { ReactNode } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { REPORT_PERIODS, REPORT_PERIOD_LABELS, type ReportPeriod } from '@cenox/shared';
import { cn } from '../../components/ui';
import { ReportView } from './ReportView';

function isPeriod(value: string | undefined): value is ReportPeriod {
  return (REPORT_PERIODS as readonly string[]).includes(value ?? '');
}

export function ReportsPage(): ReactNode {
  const { period } = useParams<{ period: string }>();

  // An unknown or missing period lands on Daily rather than erroring — a
  // stale bookmark should still open a useful report.
  if (!isPeriod(period)) return <Navigate to="/reports/daily" replace />;

  return (
    <>
      <nav
        className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200 pb-px"
        aria-label="Report period"
      >
        {REPORT_PERIODS.map((value) => (
          <NavLink
            key={value}
            to={`/reports/${value}`}
            className={({ isActive }) =>
              cn(
                'whitespace-nowrap rounded-t-lg px-3.5 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-b-2 border-brand-600 text-brand-700'
                  : 'border-b-2 border-transparent text-slate-500 hover:text-slate-800',
              )
            }
          >
            {REPORT_PERIOD_LABELS[value]}
          </NavLink>
        ))}
      </nav>

      {/* Keyed so switching period remounts rather than showing the previous
          period's figures under the new heading while the query refetches. */}
      <ReportView key={period} period={period} />
    </>
  );
}
